import { makeAutoObservable, runInAction } from 'mobx';
import axios from 'axios';
import { format, addDays, addSeconds, startOfMonth } from 'date-fns';
import ru from 'date-fns/locale/ru';
import { getAPIUrl } from '../api/apiUtils';
import { regionsStore } from '../stores';
import { groupIdToTitle } from '../utils/groupTitles';

export const TOPICS_THEMES = [
  { key: 'tgw_catastrophes', label: 'Катастрофы', description: 'Выраженность темы катастроф в обсуждениях (авиакатастрофа, ликвидатор, пожарный, ЧС)' },
  { key: 'tgw_security_structures', label: 'Силовые структуры', description: 'Тема силовых структур в обсуждениях (армия, военный, допрашивать, обвинительный)' },
  { key: 'tgw_authority', label: 'Власть', description: 'Выраженность темы власти в обсуждениях (госаппарат, губернатор, избирательный)' },
];


const DEFAULT_THEMES = [TOPICS_THEMES[0].key, TOPICS_THEMES[1].key];

// Функция для преобразования входной даты в формат 'YYYY-MM-DD'
function formatDate(value) {
  let date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'number') {
    const millis = value < 1e12 ? value * 1000 : value;
    date = new Date(millis);
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    const numeric = Number(trimmed);
    if (trimmed && !Number.isNaN(numeric)) {
      const millis = numeric < 1e12 ? numeric * 1000 : numeric;
      date = new Date(millis);
    } else {
      date = new Date(value);
    }
  }

  if (!date || Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString().split('T')[0];
}

// Функция для группировки и преобразования данных с усреднением по датам
function transformData(inputData, selectedThemes, groupMode, selectedTheme) {
  if (!Array.isArray(inputData)) {
    console.error('Ошибка: inputData не является массивом', inputData);
    return [];
  }

  if (groupMode === 'sum') {
    // группировка по темам
    const groupedByTheme = selectedThemes.reduce((acc, theme) => {
      acc[theme] = {};
      return acc;
    }, {});

    inputData.forEach(item => {
      if (!item || !item.date) {
        console.warn('Некорректный элемент данных:', item);
        return;
      }

      const formattedDate = formatDate(item.date);
      selectedThemes.forEach(theme => {
        if (item[theme] !== undefined) {
          if (!groupedByTheme[theme][formattedDate]) {
            groupedByTheme[theme][formattedDate] = { sum: 0, count: 0 };
          }
          groupedByTheme[theme][formattedDate].sum += item[theme];
          groupedByTheme[theme][formattedDate].count += 1;
        }
      });
    });

    return Object.keys(groupedByTheme).map(theme => ({
      data: Object.keys(groupedByTheme[theme]).map(date => ({
        date,
        value: groupedByTheme[theme][date].sum / groupedByTheme[theme][date].count
      })),
      marker: theme
    }));
  } else {
    // группировка по group_id
    const groupedByGroupId = inputData.reduce((acc, item) => {
      if (!item || !item.group_id || !item.date || item[selectedTheme] === undefined) {
        console.warn('Некорректный элемент данных:', item);
        return acc;
      }

      const { group_id, date, [selectedTheme]: value } = item;
      const formattedDate = formatDate(date);

      if (!acc[group_id]) {
        acc[group_id] = {};
      }
      if (!acc[group_id][formattedDate]) {
        acc[group_id][formattedDate] = { sum: 0, count: 0 };
      }
      acc[group_id][formattedDate].sum += value;
      acc[group_id][formattedDate].count += 1;

      return acc;
    }, {});

    return Object.keys(groupedByGroupId).map(group_id => {
      const title = groupIdToTitle[group_id] || group_id.toString();
      return {
        data: Object.keys(groupedByGroupId[group_id]).map(date => ({
          date,
          value: groupedByGroupId[group_id][date].sum / groupedByGroupId[group_id][date].count
        })),
        marker: title
      };
    });
  }
}

class TopicsTrendsStore {
  aggregation = 1;
  selectedThemes = DEFAULT_THEMES.slice();
  groupMode = 'sum';
  selectedTheme = TOPICS_THEMES[0].key;
  chartsData = [];
  loading = true;
  error = null;
  globalFilters = { selectedGroups: [], startDate: null, endDate: null };
  globalGroups = [];
  lastQueryStartDate = null;
  lastQueryEndDate = null;
  lastQueryAggregation = null;
  lastQueryGroups = [];
  lastQueryThemes = [];
  lastQueryGroupMode = null;
  lastEndQuery = null;

  constructor() {
    makeAutoObservable(this);
  }

  setAggregation(val) { this.aggregation = val; }
  setThemes(val) { this.selectedThemes = val; }
  setGroupMode(val) { this.groupMode = val; }
  setSelectedTheme(val) { this.selectedTheme = val; }
  setGlobalFilters(val) { this.globalFilters = val; }
  setGlobalGroups(val) { this.globalGroups = val; }

  setLastQueryParams({ startDate, endDate, aggregation, selectedGroups, selectedThemes, groupMode }) {
    this.lastQueryStartDate = startDate;
    this.lastQueryEndDate = endDate;
    this.lastQueryAggregation = aggregation;
    this.lastQueryGroups = selectedGroups;
    this.lastQueryThemes = selectedThemes;
    this.lastQueryGroupMode = groupMode;
  }

  async fetchGroups() {
    try {
      await regionsStore.fetchRegions();
      const allGroups = regionsStore.allGroups.map(g => ({
        id: String(g.group_id),
        name: g.title
      }));
      runInAction(() => { this.globalGroups = allGroups; });
    } catch {
      runInAction(() => { this.globalGroups = []; });
    }
  }

  async fetchTopicsTrendsData(start, end) {
    // console.log('num_start:', start, 'num_end:', end);

    // Собираем текущие параметры
    const currentParams = {
      startDate: new Date(this.globalFilters.startDate),
      endDate: new Date(this.globalFilters.startDate),
      aggregation: this.aggregation,
      selectedGroups: this.globalFilters.selectedGroups.slice(),
      selectedThemes: this.selectedThemes.slice(),
      groupMode: this.groupMode
    };

    // Проверяем, изменились ли параметры
    const changed =
      this.lastQueryAggregation !== currentParams.aggregation ||
      JSON.stringify(this.lastQueryGroups) !== JSON.stringify(currentParams.selectedGroups) ||
      JSON.stringify(this.lastQueryThemes) !== JSON.stringify(currentParams.selectedThemes) ||
      this.lastQueryGroupMode !== currentParams.groupMode ||
      new Date(this.lastQueryStartDate).getTime() !== currentParams.startDate.getTime() ||
      new Date(this.lastQueryEndDate).getTime() !== currentParams.endDate.getTime();

    if (changed) {
      runInAction(() => {
        this.chartsData = []; // Сброс при изменении параметров
        this.lastEndQuery = null;
        // console.log('СБРОС');
        this.loading = true;
      });
    }

    // Определяем startDate с учётом lastEndQuery
    let startDate = new Date(currentParams.startDate);
    startDate.setDate(startDate.getDate() + Number(start * this.aggregation));

    let endDate = new Date(currentParams.startDate);
    
    endDate.setDate(endDate.getDate() + Number(end * this.aggregation) + 200);

    if (this.globalFilters.endDate && endDate.getTime() > new Date(this.globalFilters.endDate).getTime()) {
      endDate = new Date(this.globalFilters.endDate);
    }
    

    runInAction(() => {
      this.error = null;
    });

    try {
      const { selectedGroups } = this.globalFilters;
      if (!selectedGroups.length || !startDate || !endDate) {
        runInAction(() => {
          this.chartsData = [];
        });
        // console.log('return1')
        return;
      }

      let intervals = [];
      let currentDate = new Date(startDate);
      const endDateObj = new Date(endDate);
      while (currentDate <= endDateObj) {
        const intervalEnd = addSeconds(addDays(currentDate, this.aggregation), -1);
        const effectiveEnd = intervalEnd > endDateObj ? endDateObj : intervalEnd;
        intervals.push({
          start: Math.floor(currentDate.getTime() / 1000),
          end: Math.floor(effectiveEnd.getTime() / 1000),
          label:
            this.aggregation === 1
              ? format(currentDate, 'yyyy-MM-dd', { locale: ru })
              : `${format(currentDate, 'yyyy-MM-dd', { locale: ru })} - ${format(effectiveEnd, 'yyyy-MM-dd', { locale: ru })}`,
        });
        currentDate = addDays(currentDate, this.aggregation);
      }

      const aggregatedData = {};
      const aggregatedCounts = {};
      if (this.groupMode === 'sum') {
        intervals.forEach(interval => {
          aggregatedData[interval.label] = {};
          aggregatedCounts[interval.label] = {};
          this.selectedThemes.forEach(theme => {
            aggregatedData[interval.label][theme] = 0;
            aggregatedCounts[interval.label][theme] = 0;
          });
        });
      } else {
        selectedGroups.forEach(groupId => {
          aggregatedData[groupId] = {};
          aggregatedCounts[groupId] = {};
          intervals.forEach(interval => {
            aggregatedData[groupId][interval.label] = {};
            aggregatedCounts[groupId][interval.label] = {};
            aggregatedData[groupId][interval.label][this.selectedTheme] = 0;
            aggregatedCounts[groupId][interval.label][this.selectedTheme] = 0;
          });
        });
      }
      if (format(new Date(this.lastEndQuery ? this.lastEndQuery : startDate), 'yyyy-MM-dd') >= format(new Date(endDate), 'yyyy-MM-dd')){
        // console.log('return2')
        return;
      }
      else{
        this.loading = true;
      }
      const params = {
        group_ids: selectedGroups.join(','),
        sort_by: 'date',
        sort_order: 'asc',
        columns:
          this.groupMode === 'sum'
            ? [...this.selectedThemes, 'date'].join(',')
            : [this.selectedTheme, 'date', 'group_id'].join(','),
        start_date: format(new Date(this.lastEndQuery ? this.lastEndQuery : new Date(this.globalFilters.startDate)), 'yyyy-MM-dd'),
        end_date: format(new Date(endDate), 'yyyy-MM-dd'),
        limit: 10000000
      };

      // console.log('Отправляем запрос, параметры:', params);
      const response = await axios.get(`${getAPIUrl()}/detection/text_markers_analysis`, { params });

      const data = response.data?.data || [];
      // console.log('сервер вернул:', data);

      // Преобразуем данные и сохраняем в chartsData
      let transformedData = transformData(data, this.selectedThemes, this.groupMode, this.selectedTheme);
      if (this.aggregation === 7) {
        transformedData = transformedData.map(series => {
          const weeklyData = {};
          series.data.forEach(point => {
            const date = new Date(point.date);
            // Определяем начало недели (понедельник)
            const weekStart = new Date(date);
            weekStart.setDate(date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1));
            const weekEnd = addDays(weekStart, 6);
            const weekLabel = `(${format(weekStart, 'yyyy-MM-dd', { locale: ru })} - ${format(weekEnd, 'yyyy-MM-dd', { locale: ru })})`;

            if (!weeklyData[weekLabel]) {
              weeklyData[weekLabel] = { sum: 0, count: 0 };
            }
            weeklyData[weekLabel].sum += point.value;
            weeklyData[weekLabel].count += 1;
          });

          return {
            ...series,
            data: Object.keys(weeklyData).map(weekLabel => ({
              date: weekLabel,
              value: weeklyData[weekLabel].sum / weeklyData[weekLabel].count
            }))
          };
        });
      }
      if (this.aggregation === 30) {
        transformedData = transformedData.map(series => {
          const monthlyData = {};
          series.data.forEach(point => {
            const date = new Date(point.date);
            // Определяем начало месяца
            const monthStart = startOfMonth(date);
            const monthLabel = format(monthStart, 'LLLL yyyy', { locale: ru });

            if (!monthlyData[monthLabel]) {
              monthlyData[monthLabel] = { sum: 0, count: 0 };
            }
            monthlyData[monthLabel].sum += point.value;
            monthlyData[monthLabel].count += 1;
          });

          return {
            ...series,
            data: Object.keys(monthlyData).map(monthLabel => ({
              date: monthLabel,
              value: monthlyData[monthLabel].sum / monthlyData[monthLabel].count
            }))
          };
        });
      }
      // console.log('transformData:', transformedData);

      runInAction(() => {
        // Если данные пустые – просто пишем
        if (this.chartsData.length === 0) {
          this.chartsData = transformedData;
        } else {
          const merged = [...this.chartsData];

          transformedData.forEach(newSeries => {
            const existingSeries = merged.find(s => s.marker === newSeries.marker);
            if (existingSeries) {
              // объединяем по дате
              const existingDates = new Set(existingSeries.data.map(d => d.date));
              newSeries.data.forEach(point => {
                if (!existingDates.has(point.date)) {
                  existingSeries.data.push(point);
                }
              });
              // сортируем по дате
              existingSeries.data.sort((a, b) => new Date(a.date.split('(')[1]?.split(' - ')[0] || a.date) - new Date(b.date.split('(')[1]?.split(' - ')[0] || b.date));
            } else {
              merged.push(newSeries);
            }
          });

          this.chartsData = merged;
        }

        // Сохраняем end_date текущего запроса как lastEndQuery
        if (this.lastEndQuery == null || this.lastEndQuery.getTime() < endDate.getTime()) {
          this.lastEndQuery = endDate;
        }
        this.setLastQueryParams(currentParams);
        this.loading = false;
      });

    } catch (e) {
      runInAction(() => {
        this.error = 'Ошибка загрузки данных: ' + (e.response?.data?.error || e.message);
        this.chartsData = [];
        this.loading = false;
      });
    } finally {
      runInAction(() => {
        // console.log("FALSE");
        // this.loading = false;
      });
    }
  }
}

export const topicsTrendsStore = new TopicsTrendsStore();